import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { verifyTicketCode } from "../lib/api";
import type { TicketVerificationResult } from "../lib/types";

function formatDate(value?: string, timezone?: string): string {
  if (!value) return "Date to be announced";
  try {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "full",
      timeStyle: "short",
      timeZone: timezone || "America/New_York"
    }).format(new Date(value));
  } catch {
    return "Date to be announced";
  }
}

export default function TicketVerifyPage({ ticketCode }: { ticketCode: string }) {
  const [result, setResult] = useState<TicketVerificationResult>();
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    verifyTicketCode(ticketCode).then(setResult).catch(() => setError("Ticket lookup is temporarily unavailable."));
  }, [ticketCode]);

  const qrValue = result?.qrPayload ?? result?.ticketUrl ?? ticketCode;
  const venue = [result?.venueName, result?.venueAddress, [result?.eventCity, result?.eventRegion].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" - ");
  const isUsable = result?.valid && !result.checkedIn;
  const statusLabel = !result ? "Checking" : result.valid ? (result.checkedIn ? "Already checked in" : "Valid") : "Not found";

  return (
    <section className="page auth-page ticket-page">
      <div className="auth-panel ticket-panel">
        <div>
          <img className="ticket-page__logo" src="/antix-logo.png" alt="AnTix logo" />
          <span className="eyebrow">AnTix mobile ticket</span>
          <h1>{result?.eventTitle ?? "Ticket lookup"}</h1>
          <span className={`pill ${isUsable ? "success" : ""}`}>{statusLabel}</span>
        </div>

        {error && <p className="form-error">{error}</p>}
        {!result && !error && <p>Checking ticket code...</p>}

        {result && !result.valid && !result.eventTitle && (
          <p className="form-error">This ticket code was not found. Check the link and contact the organizer if it still fails.</p>
        )}

        {result?.eventTitle && (
          <>
            <div className="ticket-qr">
              <QRCodeSVG value={qrValue} size={220} level="M" marginSize={2} />
            </div>
            <div className="ticket-info-grid">
              <span>Ticket code</span>
              <strong>{result.ticketCode}</strong>
              <span>Tier</span>
              <strong>{result.ticketTierName ?? "General admission"}</strong>
              <span>When</span>
              <strong>{formatDate(result.eventStartsAt, result.eventTimezone)}</strong>
              {result.eventDoorsAt && (
                <>
                  <span>Doors</span>
                  <strong>{formatDate(result.eventDoorsAt, result.eventTimezone)}</strong>
                </>
              )}
              {venue && (
                <>
                  <span>Where</span>
                  <strong>{venue}</strong>
                </>
              )}
              {result.ageRequirement && (
                <>
                  <span>Age</span>
                  <strong>{result.ageRequirement}</strong>
                </>
              )}
              {result.dressCode && (
                <>
                  <span>Dress</span>
                  <strong>{result.dressCode}</strong>
                </>
              )}
            </div>
            <p className="ticket-note">Show this QR code at entry. Screenshots work, but a live ticket page shows the latest status.</p>
          </>
        )}
      </div>
    </section>
  );
}
